<template>
  <div class="erp-entity-grid card-block">
    <div class="grid-toolbar">
      <button class="icon-btn erp-refresh-btn" :disabled="busy" @click="onRefresh">
        <img src="~@/assets/images/icons/refresh_map.svg" class="erp-refresh-icon" :class="{ 'is-spinning': busy }" />
      </button>
      <button
        v-if="showCleanButton"
        class="btn btn-sm btn-outline-warning"
        :disabled="busy || cleaning"
        @click="$emit('clean')"
      >
        {{ $t("tools.erp_integration.clean_records_button") }}
      </button>
      <button class="btn btn-sm btn-outline-primary" :disabled="busy" @click="$emit('sync')">
        {{ $t("tools.erp_integration.sync_from_endpoint") }}
      </button>
    </div>

    <div v-if="gridColumns.length === 0" class="grid-empty-config text-muted">
      {{ $t("tools.erp_integration.no_display_columns_selected") }}
    </div>

    <DxDataGrid
      v-else
      ref="dxGrid"
      class="smagria-grid erp-entity-records-grid"
      :key="gridInstanceKey"
      key-expr="id"
      :data-source="dataSource"
      :columns="gridColumns"
      :remote-operations="remoteOperations"
      :column-min-width="110"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="false"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :no-data-text="$t('common.no_data_text')"
      style="min-height: 360px"
    >
      <DxPaging :page-size="25" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[10, 25, 50, 100]"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="false" />
      <DxGroupPanel :visible="false" />
      <DxExport :enabled="false" />
      <DxColumnChooser :enabled="false" />
      <DxColumnFixing :enabled="true" />
    </DxDataGrid>
  </div>
</template>

<script>
import DxDataGrid, {
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxSearchPanel,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import axios from "@/plugins/axios";
import * as endpoints from "@/store/api/endpoints";

export default {
  name: "ErpEntityGrid",
  components: {
    DxDataGrid,
    DxPaging,
    DxPager,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxSearchPanel,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
  },
  props: {
    entity: {
      type: Object,
      default: () => null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    cleaning: {
      type: Boolean,
      default: false,
    },
    showCleanButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      remoteOperations: { paging: true, filtering: true, sorting: true, grouping: true },
    };
  },
  computed: {
    gridInstanceKey() {
      const code = this.entity?.code || "NO_ENTITY";
      const fieldKey = this.gridColumns.map((item) => item.dataField).join("|") || "NO_COLUMNS";
      return `${code}-${fieldKey}`;
    },
    dataSource() {
      const entityCode = this.entity?.code;

      if (!entityCode) {
        return [];
      }

      const url = endpoints.replaceParams(endpoints.ERP_ENTITY_RECORDS, { entityCode });

      return new CustomStore({
        key: "id",
        load: async (loadOptions) => {
          const limit = loadOptions.take || 25;
          const skip = loadOptions.skip ?? 0;
          const page = Math.floor(skip / limit) + 1;

          const params = { page, limit };

          const sortInfo = Array.isArray(loadOptions.sort) && loadOptions.sort.length > 0
            ? loadOptions.sort[0] : null;
          if (sortInfo?.selector) {
            params.sort_by = sortInfo.selector;
            params.sort_desc = sortInfo.desc ? 1 : 0;
          }

          if (loadOptions.filter) {
            params.filter = JSON.stringify(loadOptions.filter);
          }

          const groupInfo = Array.isArray(loadOptions.group) ? loadOptions.group[0] : null;
          if (groupInfo?.selector) {
            params.group_by = groupInfo.selector;
          }

          const response = await axios.get(url, { params });

          const data = response?.data?.data?.data || [];
          const total = response?.data?.data?.pagination?.total || 0;

          if (params.group_by) {
            return { data, totalCount: total, groupCount: total };
          }

          return {
            data: data.map((item) => ({ id: item.id, ...(item.raw_data || {}) })),
            totalCount: total,
          };
        },
      });
    },
    gridColumns() {
      const configuredColumns = Array.isArray(this.entity?.display_columns) ? this.entity.display_columns : [];

      return [...configuredColumns]
        .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0))
        .map((item) => ({
          dataField: item?.column,
          caption: item?.label || item?.column,
          allowHeaderFiltering: true,
          filterOperations: ["contains"],
          allowSorting: true,
        }))
        .filter((item) => Boolean(item.dataField));
    },
  },
  methods: {
    reload() {
      this.$refs.dxGrid?.instance?.refresh();
    },
    onRefresh() {
      this.$emit("refresh");
    },
  },
};
</script>

<style scoped>
.erp-entity-records-grid {
  height: auto;
}

.erp-entity-records-grid ::v-deep .dx-datagrid-rowsview .dx-data-row {
  height: 36px;
  min-height: 36px;
}

.erp-entity-records-grid ::v-deep .dx-datagrid-rowsview .dx-row > td {
  padding-top: 4px;
  padding-bottom: 4px;
}

.card-block {
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}

.grid-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.erp-refresh-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #c6d4e7;
  border-radius: 6px;
  background: #fff;
}

.erp-refresh-icon {
  width: 14px;
  height: 14px;
}

.is-spinning {
  animation: spin 1s linear infinite;
}
</style>

<style scoped>
.card-block {
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}

.grid-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.erp-refresh-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #c6d4e7;
  border-radius: 6px;
  background: #fff;
}

.erp-refresh-icon {
  width: 14px;
  height: 14px;
}

.is-spinning {
  animation: spin 1s linear infinite;
}

.grid-empty-config {
  border: 1px dashed #c7d3e4;
  border-radius: 8px;
  padding: 14px;
  background: #f8fbff;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
